/**
 * Work Plan — All active (non-closed) privacy incidents.
 * Shows: title, status, discovery date, days to/over deadline, assignees, department, root cause.
 * Sort and filter by status, deadline, department.
 */

import React, { useState, useEffect, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  draft: 'Draft',
  under_investigation: 'Under investigation',
  risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval',
  approved_not_breach: 'Not a breach',
  approved_breach: 'Breach',
  notification_complete: 'Notification complete',
  closed: 'Closed'
};

const ROOT_CAUSE_LABELS = {
  human_error: 'Human error',
  system_failure: 'System failure',
  third_party: 'Third party',
  theft_loss: 'Theft/Loss',
  improper_disposal: 'Improper disposal',
  unknown: 'Unknown',
  other: 'Other'
};

function daysUntil(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d - t) / (24 * 60 * 60 * 1000));
  return diff;
}

// The notification clock runs from the date the breach was known or should
// have been known (breach_discovered_date, 45 CFR 164.404(a)(2)); the
// determination date is only a legacy fallback. Prefer the stored deadline,
// which the obligations engine computed across all mapped states (a state
// clock may be shorter than the federal 60 days).
function breachDeadlineDays(inc) {
  if (!inc) return null;
  if (inc.notification_deadline_date) return daysUntil(new Date(inc.notification_deadline_date));
  const anchor = inc.breach_discovered_date || inc.breach_determined_date;
  if (!anchor) return null;
  const due = new Date(new Date(anchor).getTime() + 60 * 24 * 60 * 60 * 1000);
  return daysUntil(due);
}

const SORT_OPTIONS = [
  { value: 'discovery_date', label: 'Discovery date' },
  { value: 'days_to_deadline', label: 'Days to deadline' },
  { value: 'status', label: 'Status' },
  { value: 'title', label: 'Title' }
];

const DEADLINE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'due_soon', label: 'Due within 14 days' },
  { value: 'on_track', label: 'On track' }
];

export default function PrivacyWorkPlan({ onOpenIncident }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDiscoveryDate, setNewDiscoveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [deadlineFilter, setDeadlineFilter] = useState('all');
  const [sortBy, setSortBy] = useState('discovery_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/active`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setIncidents(data.incidents || []);
      else setError(data.message || 'Failed to load');
    } catch (e) {
      setError(e.message || 'Failed to load');
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const departments = useMemo(() => {
    const set = new Set();
    incidents.forEach((inc) => {
      const d = (inc.department_name || '').trim();
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [incidents]);

  const filteredAndSortedIncidents = useMemo(() => {
    let list = [...incidents];
    if (statusFilter !== 'all') list = list.filter((inc) => inc.status === statusFilter);
    if (departmentFilter !== 'all') list = list.filter((inc) => (inc.department_name || '').trim() === departmentFilter);
    if (deadlineFilter !== 'all') {
      list = list.filter((inc) => {
        const days = breachDeadlineDays(inc);
        if (days == null) return deadlineFilter === 'on_track';
        if (deadlineFilter === 'overdue') return days < 0;
        if (deadlineFilter === 'due_soon') return days >= 0 && days <= 14;
        if (deadlineFilter === 'on_track') return days > 14;
        return true;
      });
    }
    const getDays = (inc) => breachDeadlineDays(inc);
    const getSortValue = (inc) => {
      switch (sortBy) {
        case 'discovery_date':
          return inc.discovery_date || '';
        case 'days_to_deadline': {
          const d = getDays(inc);
          return d == null ? 999999 : d;
        }
        case 'status':
          return STATUS_LABELS[inc.status] || inc.status || '';
        case 'department':
          return (inc.department_name || '').toLowerCase();
        case 'title':
          return (inc.title || '').toLowerCase();
        default:
          return inc.discovery_date || '';
      }
    };
    list.sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      const order = sortBy === 'days_to_deadline' ? (sortOrder === 'desc' ? 'asc' : 'desc') : sortOrder;
      return order === 'desc' ? -cmp : cmp;
    });
    return list;
  }, [incidents, statusFilter, departmentFilter, deadlineFilter, sortBy, sortOrder]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), discovery_date: newDiscoveryDate })
      });
      const data = await res.json();
      if (data.success && data.incident) {
        setShowNewForm(false);
        setNewTitle('');
        setNewDiscoveryDate(new Date().toISOString().slice(0, 10));
        onOpenIncident(data.incident.id);
      } else {
        setError(data.message || 'Create failed');
      }
    } catch (e) {
      setError(e.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-blue-50/60 shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-600 to-amber-500" />
          <div className="p-6 sm:p-7 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">Active privacy incidents</h2>
              <p className="text-slate-500 text-sm mt-1">
                Open incidents under investigation. The 60-day notification clock starts when a breach is confirmed (45 CFR §164.404).
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <a
                href={`${API}/api/privacy/case-template`}
                className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 shadow-sm transition-colors"
                title="A blank Word workbook to track a case step by step, from overview through risk, notifications, and closure"
              >
                Case tracking template
              </a>
              <button
                type="button"
                onClick={() => setShowNewForm(true)}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 shadow-md shadow-blue-600/20 transition-opacity"
              >
                + New incident
              </button>
            </div>
          </div>
        </div>

        {showNewForm && (
          <form onSubmit={handleCreate} className="mb-6 p-6 bg-white rounded-2xl border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-3">Log new incident</h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Brief description"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Discovery date *
                  <span className="ml-1 text-slate-400 font-normal" title="When your organization first learned of the incident (45 CFR §164.402)">ⓘ</span>
                </label>
                <input
                  type="date"
                  value={newDiscoveryDate}
                  onChange={(e) => setNewDiscoveryDate(e.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button type="submit" disabled={creating} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowNewForm(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {loading && <div className="text-slate-500 py-8">Loading...</div>}

        {!loading && incidents.length === 0 && !showNewForm && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
            No active incidents. Click &quot;New incident&quot; to log one.
          </div>
        )}

        {!loading && incidents.length > 0 && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Filters</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All statuses</option>
                {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'closed').map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select
                value={deadlineFilter}
                onChange={(e) => setDeadlineFilter(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {DEADLINE_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 ml-2">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              <span className="text-slate-500 text-sm">
                {filteredAndSortedIncidents.length} of {incidents.length} incident{incidents.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: 900 }}>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                      <th className="py-3.5 px-5 w-24">ID</th>
                      <th className="py-3.5 px-5">Title</th>
                      <th className="py-3.5 px-5">Status</th>
                      <th className="py-3.5 px-5">Discovery</th>
                      <th className="py-3.5 px-5">Days to deadline</th>
                      <th className="py-3.5 px-5 w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAndSortedIncidents.map((inc) => {
                    const days = breachDeadlineDays(inc);
                    const overdue = days != null && days < 0;
                    const soon = days != null && days >= 0 && days <= 14;
                    return (
                      <tr key={inc.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3.5 px-5 text-slate-500 font-mono text-xs">{inc.id ? String(inc.id).slice(0, 8) : '—'}</td>
                        <td className="py-3.5 px-5">
                          <button type="button" onClick={() => onOpenIncident(inc.id)} className="text-left font-medium text-slate-900 hover:text-blue-600 transition-colors">
                            {inc.title || 'Untitled'}
                          </button>
                        </td>
                        <td className="py-3.5 px-5">
                          <span className="inline-flex px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700">
                            {STATUS_LABELS[inc.status] || inc.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-5 text-slate-600">{inc.discovery_date || '—'}</td>
                        <td className="py-3.5 px-5">
                          {days != null ? (
                            <span className={overdue ? 'text-red-600 font-medium' : soon ? 'text-amber-600 font-medium' : 'text-slate-600'}>
                              {overdue ? `${Math.abs(days)} over` : `${days} days`}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-3.5 px-5">
                          <button type="button" onClick={() => onOpenIncident(inc.id)} className="text-blue-600 font-medium hover:text-blue-700 hover:underline transition-colors">
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
            {filteredAndSortedIncidents.length === 0 && (
              <p className="mt-3 text-slate-500 text-sm">No incidents match the current filters.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
